import { Component, ViewChild } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import type { SwiperOptions } from 'swiper/types/swiper-options';
import { FtSwiperDirective } from 'apps/account/src/account-shared/modules/swiper/ft-swiper.directive';
import { Subject } from 'rxjs';
import { first } from 'rxjs/operators';
import { TransactionsImportResult, TransactionsImportService } from '../../services/transactions-import.service';

const REQUIRED_HEADERS = [
    'transaction_datetime',
    'crs_number',
    'company_code',
    'amount',
    'currency',
    'arrival_date',
    'departure_date'
];

@Component({
    selector: 'ft-transactions-import',
    templateUrl: './transactions-import.component.html',
    styleUrls: ['./transactions-import.component.scss']
})
export class TransactionsImportComponent {
    @ViewChild('swiper', { read: FtSwiperDirective }) swiperDir: FtSwiperDirective;
    slideIndex = 0;
    totalSlides = 3;
    slides = Array.from({ length: 3 }, (_, i) => i);
    readonly swiperConfig: SwiperOptions = {
        simulateTouch: false,
        speed: 800,
        allowTouchMove: false,
    };
    files: File[] = [];
    error: { status?: number; message: string } | null = null;
    headerValidationError: string | null = null;
    loading$ = new Subject<boolean>();
    isUploadComplete = false;
    result: TransactionsImportResult | null = null;

    constructor(public dialogRef: MatDialogRef<TransactionsImportComponent>,
                private importService: TransactionsImportService) {
    }

    onSlideChange(e: CustomEvent) {
        this.slideIndex = (e as any).detail?.[0]?.realIndex ?? this.slideIndex;
    }

    nextSlide() {
        this.swiperDir?.slideNext();
    }

    goToSlide(idx: number) {
        this.swiperDir?.slideTo(idx);
    }

    onClose() {
        this.dialogRef.close();
    }

    onFilesSelected(files: File[]) {
        this.files = files || [];
        this.error = null;
        this.headerValidationError = null;
        if (this.files[0]) {
            this.validateHeaders(this.files[0]);
        }
    }

    private validateHeaders(file: File) {
        const reader = new FileReader();
        reader.onload = () => {
            const text = (reader.result as string) || '';
            const firstLine = text.split(/\r?\n/, 1)[0] || '';
            const headers = firstLine.split(',').map(h => h.trim().toLowerCase());
            const missing = REQUIRED_HEADERS.filter(h => !headers.includes(h));
            this.headerValidationError = missing.length > 0
                ? `Missing required columns: ${missing.join(', ')}`
                : null;
        };
        reader.readAsText(file.slice(0, 4096));
    }

    submit() {
        if (!this.files.length || this.headerValidationError) return;
        this.loading$.next(true);
        this.error = null;
        this.importService.upload(this.files[0]).pipe(first()).subscribe({
            next: result => {
                this.loading$.next(false);
                this.result = result;
                this.isUploadComplete = true;
            },
            error: err => {
                this.loading$.next(false);
                this.error = {
                    status: err?.status,
                    message: err?.error?.detail || err?.error || err?.message || 'Upload failed.'
                };
            }
        });
    }
}
